import { FunctionComponent, memo, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { ChannelContext, EventDetailsTab } from '../../context';
import {
  EventDetailsCommon,
  IconLabel,
  getCssVar,
  layoutPath,
  useAppDispatch,
  useAppSelector,
} from '../../shared';
import { eventById } from '../../slices';
import { NoContent } from '../Error';

import classes from './EventDetails.module.scss';

export const EventDetails: FunctionComponent = memo(() => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'eventDetails' });

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const eventId = Number(useParams().eventId);

  const { id: channelId } = useContext(ChannelContext).channel;

  const { event, isFetched } = useAppSelector(({ eventDetails }) => eventDetails);

  const [searchParams] = useSearchParams();

  const shouldOpenParticipants = useMemo(
    () => searchParams.get('tab') === EventDetailsTab.PARTICIPANTS,
    [searchParams]
  );

  const shouldOpenWaitlist = useMemo(
    () => searchParams.get('tab') === EventDetailsTab.WAITLIST,
    [searchParams]
  );

  useEffect(() => {
    dispatch(eventById({ channelId, eventId }));
  }, [channelId, dispatch, eventId]);

  useEffect(() => {
    if (!isFetched || !event) {
      return;
    }

    if (!event.channels.find(({ id }) => id === channelId)) {
      navigate(layoutPath('/events'));
    }
  }, [channelId, event, isFetched, navigate]);

  if (!isFetched) {
    return <Skeleton height={'16rem'} />;
  }

  if (!event) {
    return <NoContent />;
  }

  const initialTab = shouldOpenParticipants
    ? EventDetailsTab.PARTICIPANTS
    : shouldOpenWaitlist
    ? EventDetailsTab.WAITLIST
    : undefined;

  return (
    <>
      <IconLabel
        className={classes['all-events']}
        iconId={'arrow-rounded-left'}
        iconSize={18}
        label={ctxT('all-events')}
        color={getCssVar('--base-link-text-color')}
        hoverColor={getCssVar('--base-link-text-hover-color')}
        onClick={() => navigate(layoutPath('/events'))}
      />
      <EventDetailsCommon
        event={event}
        onDeleteSuccess={() => navigate(layoutPath('/events'))}
        initialTab={initialTab}
      />
    </>
  );
});
